import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type ServiceBinding,
} from "@nasam/kernel";
import {
  branchesRepositoryToken,
  companiesRepositoryToken,
  devicesRepositoryToken,
  type BranchesRepository,
  type CompaniesRepository,
  type DevicesRepository,
} from "../../contracts";
import {
  createSensorAssignmentsHttpRepository,
  createSensorAssignmentsMockRepository,
} from "./data";
import type { SensorAssignmentsRepository } from "./domain";
import { createSensorAssignmentsRoutes } from "./presentation/routes";
import { SENSORS_VIEW, SENSOR_ASSIGNMENTS_VIEW } from "./sensorAssignmentsPermissions";

export * from "./sensorAssignmentsPermissions";

export const sensorAssignmentsRepositoryToken =
  createServiceToken<SensorAssignmentsRepository>("sensorAssignments.repository");

// Every sensors endpoint is now confirmed real (2026-09-22, GET /sensors
// included -- the plain list, last trialed the same day it was still
// PLANNED, is live). Typed `boolean` (not inferred as the literal
// `false`/`true`) so flipping any of these by hand doesn't fight
// `no-unnecessary-condition`.
const USE_MOCK_SENSOR_LIST: boolean = false;
const USE_MOCK_SENSOR_FORM: boolean = false;
const USE_MOCK_SENSOR_CREATE: boolean = false;
const USE_MOCK_SENSOR_DETAIL: boolean = false;
const USE_MOCK_SENSOR_UPDATE: boolean = false;
const USE_MOCK_SENSOR_DELETE: boolean = false;
const USE_MOCK_SENSOR_DEVICE_SENSORS: boolean = false;

function createSensorAssignmentsServiceBindings(
  httpClient: Parameters<
    NonNullable<AppFeatureDefinition["provideServices"]>
  >[0]["httpClient"],
): readonly ServiceBinding[] {
  const http = createSensorAssignmentsHttpRepository(httpClient);
  const mock = createSensorAssignmentsMockRepository();

  const repository: SensorAssignmentsRepository = {
    list: (query) => (USE_MOCK_SENSOR_LIST ? mock : http).list(query),
    getForm: (sensorType) => (USE_MOCK_SENSOR_FORM ? mock : http).getForm(sensorType),
    create: (input) => (USE_MOCK_SENSOR_CREATE ? mock : http).create(input),
    getById: (sensorId) => (USE_MOCK_SENSOR_DETAIL ? mock : http).getById(sensorId),
    update: (sensorId, input) =>
      (USE_MOCK_SENSOR_UPDATE ? mock : http).update(sensorId, input),
    delete: (sensorId) => (USE_MOCK_SENSOR_DELETE ? mock : http).delete(sensorId),
    getDeviceSensors: (deviceId) =>
      (USE_MOCK_SENSOR_DEVICE_SENSORS ? mock : http).getDeviceSensors(deviceId),
  };

  return [bindService(sensorAssignmentsRepositoryToken, repository)];
}

export const sensorAssignmentsFeature: AppFeatureDefinition = {
  id: "sensorAssignments",
  version: "0.1.0",
  titleKey: "sensorAssignments:moduleTitle",
  icon: "Radio",
  basePath: "/sensor-assignments",
  requiredPermissions: [SENSOR_ASSIGNMENTS_VIEW, SENSORS_VIEW],
  // The "Device Assignment" nav node lives in devicesFeature (it points at
  // the real device-assignment feature, /devices). This feature contributes
  // the Sensor Assignment leaf, still served at its own /sensor-assignments
  // path, but nested under devicesFeature's "devices" drawer group -- same
  // id, so mergeFeatureNavNodes joins the two features' children into one
  // group instead of two separate "devices" entries.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "devices",
          titleKey: "devices:moduleTitle",
          placement: "drawer",
          requiredPermissions: [SENSORS_VIEW],
          children: [
            {
              id: "sensors-management",
              titleKey: "sensorAssignments:nav.sensorsManagement",
              icon: "Radio",
              placement: "drawer",
              order: 20,
              path: "/sensor-assignments",
              requiredPermissions: [SENSORS_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) =>
    createSensorAssignmentsRoutes({
      context,
      sensorAssignments: context.services.resolve(
        sensorAssignmentsRepositoryToken,
      ),
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      devices: context.services.tryResolve<DevicesRepository>(
        devicesRepositoryToken,
      ),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
    }),
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "sensorAssignments:permissions.core",
      children: [
        {
          segment: "sensor_assignment_management",
          labelKey: "sensorAssignments:permissions.sensorAssignmentManagement",
          children: [
            {
              segment: "sensor_assignment_list",
              labelKey: "sensorAssignments:permissions.sensorAssignments",
              actions: [
                { id: "view", labelKey: "sensorAssignments:actions.view" },
                { id: "create", labelKey: "sensorAssignments:actions.create" },
                { id: "update", labelKey: "sensorAssignments:actions.update" },
                { id: "delete", labelKey: "sensorAssignments:actions.delete" },
              ],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) =>
    createSensorAssignmentsServiceBindings(httpClient),
};
